import React from 'react';
import { motion } from 'framer-motion';
import { Download, ShoppingCart, CheckCircle, Star } from 'lucide-react';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { useLanguage } from '../contexts/LanguageContext';
import { type FileItem } from '../contexts/DataContext';
interface FileCardProps {
  file: FileItem;
  purchased?: boolean;
  onBuy: (file: FileItem) => void;
  onDownload?: (file: FileItem) => void;
}
export function FileCard({
  file,
  purchased,
  onBuy,
  onDownload
}: FileCardProps) {
  const {
    t
  } = useLanguage();
  const typeColors: Record<string, string> = {
    book: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    pastpaper: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    other: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  };
  const typeLabels: Record<string, string> = {
    book: t('market.filter.books'),
    pastpaper: t('market.filter.pastpapers'),
    other: t('market.filter.other')
  };
  const rating = file.rating || 0;
  const reviewCount = file.reviewCount || 0;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} whileHover={{
    y: -4,
    transition: {
      duration: 0.2
    }
  }} transition={{
    duration: 0.3
  }} data-id="element-33">
      <Card className="overflow-hidden h-full flex flex-col group" data-id="element-34">
        {/* Cover Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-muted" data-id="element-35">
          <img src={file.coverUrl} alt={file.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" onError={e => {
          ;
          (e.target as HTMLImageElement).src = `https://picsum.photos/seed/${file.id}/400/300`;
        }} data-id="element-36" />
          <div className="absolute top-2 left-2 flex flex-wrap gap-1" data-id="element-37">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[file.type]}`} data-id="element-38">
              {typeLabels[file.type]}
            </span>
            {file.subject && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300" data-id="element-39">
                {file.subject}
              </span>}
          </div>
          {purchased && <div className="absolute top-2 right-2" data-id="element-40">
              <Badge className="bg-green-600 text-white gap-1" data-id="element-41">
                <CheckCircle className="h-3 w-3" data-id="element-42" />
                {t('market.purchased')}
              </Badge>
            </div>}
          <div className="absolute bottom-2 right-2 flex gap-1" data-id="element-43">
            {file.level && <Badge variant="secondary" className="text-[10px]" data-id="element-44">
                {file.level}
              </Badge>}
            <Badge variant="secondary" className="font-mono text-xs" data-id="element-45">
              {file.fileFormat}
            </Badge>
          </div>
        </div>

        <CardContent className="flex-1 flex flex-col p-3 sm:p-4 gap-2 sm:gap-3" data-id="element-46">
          <div className="flex-1 min-w-0" data-id="element-47">
            <h3 className="font-heading font-semibold text-xs sm:text-sm leading-tight line-clamp-2 mb-1" data-id="element-48">
              {file.name}
            </h3>
            <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2" data-id="element-49">
              {file.description}
            </p>
            {/* Rating */}
            {reviewCount > 0 && <div className="flex items-center gap-1 mt-1.5" data-id="element-50">
                <div className="flex items-center" data-id="element-51">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`h-3 w-3 ${star <= Math.round(rating) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'}`} data-id="element-52" />)}
                </div>
                <span className="text-[10px] text-muted-foreground" data-id="element-53">
                  {rating.toFixed(1)} ({reviewCount})
                </span>
              </div>}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border gap-2" data-id="element-54">
            <div className="flex items-center gap-1 min-w-0" data-id="element-55">
              <span className="text-base sm:text-lg font-bold text-blue-500" data-id="element-56">
                {file.price.toLocaleString()}
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground" data-id="element-57">
                {t('market.frw')}
              </span>
            </div>

            {purchased ? <Button size="sm" variant="outline" className="gap-1 text-xs sm:text-sm text-green-500 border-green-500 hover:bg-green-500/10 flex-shrink-0" onClick={() => onDownload?.(file)} data-id="element-58">
                <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5" data-id="element-59" />
                <span className="hidden sm:inline" data-id="element-60">{t('market.download')}</span>
                <span className="sm:hidden" data-id="element-61">Get</span>
              </Button> : <Button size="sm" className="gap-1 text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" onClick={() => onBuy(file)} data-id="element-62">
                <ShoppingCart className="h-3 w-3 sm:h-3.5 sm:w-3.5" data-id="element-63" />
                <span className="hidden sm:inline" data-id="element-64">{t('market.buy')}</span>
                <span className="sm:hidden" data-id="element-65">Buy</span>
              </Button>}
          </div>
        </CardContent>
      </Card>
    </motion.div>;
}